import { cloneDeep } from '@fi-shared/utils';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiFormHelperService } from '@fi-app/shared/fi-form-shared/fi-form-helper.service';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    Injector,
    ViewEncapsulation,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils/is-defined';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { IFormEdit, IFormQuestion } from '@fi-shared/fi-form-shared/fi-form-shared';
import { FormQuestionType } from '@fi-shared/fi-form-shared/fi-form-enums';
import { IFormQuestionViewData } from '@fi-app/fi-form/fi-form';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiChoiceQuestionComponent } from '@fi-app/fi-form/fi-form-edit/fi-form-edit-form/fi-form-choice-question/fi-choice-question.component';
import { removeItem } from '@fi-shared/utils/remove-item';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-form-edit-form',
    templateUrl: './fi-form-edit-form.component.html',
    styleUrls: ['./fi-form-edit-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiFormEditFormComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiFormEditFormComponent,
        },
    ],
    host: { class: 'fi-form-edit-form' },
    standalone: false,
})
export class FiFormEditFormComponent extends ControlValueAccessorValidatorBase<IFormEdit> implements OnInit {
    @ViewChild('fiChoiceQuestionComponent') fiChoiceQuestionComponent: FiChoiceQuestionComponent;
    @Input() selectedLanguageId: number;
    @Input() defaultLanguageId: number;
    @Input() isValidationErrorOnFormSave: boolean;
    @Input() isFormTranslated: boolean;
    @Output() onLanguageChangeEvent = new EventEmitter<number>();
    @ViewChild('questionsListScrollableContainer') questionsListScrollableContainer: ElementRef;

    get form() {
        return this.ngModel;
    }

    constructor(
        injector: Injector,
        private formHelperService: FiFormHelperService,
        private contentLanguagesService: ContentLanguagesService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
    ) {
        super(injector);
    }

    ngOnInit() {
        // we should add non existing content language labels to question names, preset option names etc
        this.initializeQuestionsContentLanguagesArray();
    }

    onLanguageChange($event: number) {
        this.onLanguageChangeEvent.emit($event);
    }

    getAnswerWrapperId(question: IFormQuestion): string {
        return `answer-wrapper-${question.uid}`;
    }

    deleteQuestion(question: IFormQuestion) {
        removeItem(this.form.Questions, question);
        this.doPropagateChanges();
        this.formHelperService.triggerDeleteQuestionEvent();
    }

    getQuestionIndex(index: number): string | number {
        return index + 1 > 9 ? index + 1 : `0${index + 1}`;
    }

    toggleQuestionMandatoryStatus(question: IFormQuestion) {
        question.IsMandatory = !question.IsMandatory;
    }

    toggleQuestionDisabledStatus(question: IFormQuestion) {
        question.IsDisabled = !question.IsDisabled;
    }

    isTextOrNumericQuestion(question: IFormQuestion): boolean {
        return this.formHelperService.isTextOrNumericQuestion(question);
    }

    isSingleOrMultipleChoiceQuestion(question: IFormQuestion): boolean {
        return this.formHelperService.isChoiceQuestion(question);
    }

    isAdditionalTextElement(question: IFormQuestion): boolean {
        return (
            this.formHelperService.isHeadingElement(question) || this.formHelperService.isTextMessageElement(question)
        );
    }

    isMediaBankElement(question: IFormQuestion): boolean {
        return this.formHelperService.isMediaBankElement(question);
    }

    isFileAttachmentElement(question: IFormQuestion): boolean {
        return this.formHelperService.isFileAttachmentElement(question);
    }

    onElementDrop($event: CdkDragDrop<IFormQuestion[], IFormQuestion[] | IFormQuestionViewData[]>) {
        if ($event.previousContainer === $event.container) {
            moveItemInArray($event.container.data, $event.previousIndex, $event.currentIndex);
        } else {
            const newQuestionType: number = (
                $event.previousContainer.data[$event.previousIndex] as IFormQuestionViewData
            ).type;
            this.addFormElement(this.formHelperService.getNewQuestion(newQuestionType), $event.currentIndex);
        }
    }

    private isNoQuestions(): boolean {
        return !this.form.Questions.some(
            (question: IFormQuestion) =>
                question.Type !== FormQuestionType.TextMessage &&
                question.Type !== FormQuestionType.Heading &&
                question.Type !== FormQuestionType.MediaBank,
        );
    }

    isNoQuestionsErrorShown(): boolean {
        return this.isNoQuestions() && this.isValidationErrorOnFormSave;
    }

    onFormElementClick($event: IFormQuestionViewData) {
        const item: IFormQuestion = this.formHelperService.getNewQuestion($event.type);
        this.addFormElement(item, this.form.Questions.length);
    }

    private addFormElement(item: IFormQuestion, insertIndex: number) {
        this.contentLanguagesService.InitializeContentLanguages(
            item,
            cloneDeep({
                Names: item.Names,
                Descriptions: [],
            }),
        );

        if (this.isFileAttachmentElement(item))
            this.contentLanguagesService.InitializeContentLanguages(
                item.UploadButton,
                cloneDeep({
                    Names: item.UploadButton.Names,
                }),
            );

        this.form.Questions.splice(insertIndex, 0, item);

        setTimeout(() => {
            this.doPropagateChanges();
            let newItemElement: HTMLElement = document.getElementById(`answer-wrapper-${item.uid}`);
            newItemElement.scrollIntoView({ behavior: 'smooth' });
            this.setFocusedQuestionNameUid(item.uid, newItemElement);
        });
    }

    focusedQuestionNameUid$ = new BehaviorSubject<number>(undefined);
    private setFocusedQuestionNameUid(questionUid: number, focusElement: HTMLElement) {
        const observer: IntersectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    this.focusedQuestionNameUid$.next(questionUid);

                    setTimeout(() => {
                        this.focusedQuestionNameUid$.next(undefined);
                        observer.unobserve(focusElement);
                    });
                    return;
                }
            },
            { root: this.questionsListScrollableContainer.nativeElement, threshold: 1 },
        );
        observer.observe(focusElement);
    }

    private initializeQuestionsContentLanguagesArray() {
        this.form.Questions.forEach((question) => {
            this.contentLanguageSharedService.initializeContentLanguagesArray(question.Names, question.Names);
            question.PresetOptions.forEach((presetOption) => {
                this.contentLanguageSharedService.initializeContentLanguagesArray(
                    presetOption.Names,
                    presetOption.Names,
                );
            });
            if (isDefined(question.UploadButton)) {
                this.contentLanguageSharedService.initializeContentLanguagesArray(
                    question.UploadButton.Names,
                    question.UploadButton.Names,
                );
            }
        });
    }

    doValidate(c: AbstractControl): ValidationErrors {
        return {
            formNoQuestions: this.isNoQuestions(),
            formNameRequired: this.formHelperService.isFormNameRequired(this.form.Names),
            formQuestionNameRequired: this.form.Questions.some((formQuestion) => {
                return (
                    !this.formHelperService.isMediaBankElement(formQuestion) &&
                    formQuestion.Names.some((questionName) => {
                        return this.formHelperService.isQuestionNameRequired(questionName, this.isFormTranslated);
                    })
                );
            }),
        };
    }
}
